import React from "react";
import { cn } from "@/lib/utils";
import { VbLoading } from "@/components/vb";
import { PickerSkeleton } from "@/pages/vb/buyer-create/PickerSkeleton";
import { VerdictSkeleton } from "@/pages/vb/verdict/VerdictSkeleton";
import {
  ExposureCardSkeleton,
  Big5CoverageCardSkeleton,
} from "@/pages/vb/detail/DetailCardSkeletons";

/**
 * Route-shaped loading skeletons for the app shell (AITS-590).
 *
 * The onboarding gate used to blank the entire app to a centred "Checking
 * access…" while it resolved. It now resolves once per session, and for that
 * one cold pass the shell keeps its chrome (sidebar, banners) and swaps only
 * the routed page for a skeleton of the page that is about to appear — so the
 * layout never jumps and nothing reads as an interstitial.
 *
 * Each shape reuses the real screen's own layout classes — and, where the screen
 * already has a skeleton for its own data load, that component — rather than
 * approximating them, so the bones land where the content lands.
 *
 * Nothing enforces that: the hero and card class strings below are copied from
 * ScoreCard/NextThingCard, so a layout change there silently desyncs this. The
 * tests pin the structural classes that carry the geometry, which catches a
 * rename but not a restyle. Prefer reusing a screen's own skeleton when it has
 * one (as the Virtual Buyer shapes do) over adding a second copy here.
 */

/** A single shimmering bone. `.vb-skel` is unscoped (see vb-primitives.css). */
function Bone({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <span className={cn("vb-skel", className)} style={style} aria-hidden="true" />;
}

/** `.vb-page-hd` with a title (and optional subtitle) + right-aligned actions. */
function HeaderBones({
  titleWidth = "38%",
  subtitle = false,
  actions = 0,
  leadIcon = false,
  crumb = false,
}: {
  titleWidth?: string;
  subtitle?: boolean;
  actions?: number;
  leadIcon?: boolean;
  /** A back-link above the title (buyer detail), which pushes the title down. */
  crumb?: boolean;
}) {
  return (
    <header className="vb-page-hd">
      <div className="vb-page-hd__left">
        {crumb && <Bone className="vb-skel-line vb-skel-line--sm mb-1 w-[104px]" />}
        <div className="vb-page-hd__title">
          {leadIcon && <Bone className="h-[26px] w-[26px] rounded-vb-sm" />}
          <Bone className="h-[22px]" style={{ width: titleWidth, minWidth: 140 }} />
        </div>
        {subtitle && <Bone className="vb-skel-line vb-skel-line--sm mt-1 w-[52%] max-w-[420px]" />}
      </div>
      {actions > 0 && (
        <div className="vb-page-hd__actions">
          {Array.from({ length: actions }).map((_, i) => (
            <Bone key={i} className="h-9 w-[104px] rounded-vb-md" />
          ))}
        </div>
      )}
    </header>
  );
}

/**
 * Report dashboard — header, the score/next-thing hero, the tier accordion with
 * its signal-card grid, and the reputation readout.
 */
function DashboardSkeleton() {
  return (
    <div className="vb-page">
      <HeaderBones titleWidth="30%" leadIcon actions={3} />

      <div className="space-y-8">
        {/* Hero: score card (left) + next thing (right, ~1:1.35), as Projects.tsx. */}
        <div className="grid grid-cols-1 items-stretch gap-3.5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
          <div className="flex h-full flex-col rounded-2xl border border-vb-line bg-vb-surface p-5 shadow-vb-sm sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <Bone className="vb-skel-line vb-skel-line--sm w-[128px]" />
              <Bone className="h-8 w-[132px] rounded-vb-md" />
            </div>
            <Bone className="h-[38px] w-[92px]" />
            {/* The four-tier segmented score bar. */}
            <Bone className="mt-4 h-[26px] w-full rounded-vb-md" />
            <div className="mt-5 flex flex-col gap-2">
              <Bone className="vb-skel-line w-full" />
              <Bone className="vb-skel-line w-[94%]" />
              <Bone className="vb-skel-line w-[62%]" />
            </div>
          </div>

          <div className="flex h-full flex-col overflow-hidden rounded-2xl border-2 border-vb-brand bg-vb-surface shadow-vb-sm">
            <div className="flex flex-1 flex-col gap-3 p-7 pb-6">
              <Bone className="h-6 w-40 rounded-vb-md" />
              <Bone className="h-8 w-3/4 rounded-vb-md" />
              <Bone className="h-20 w-full rounded-[10px]" />
              <Bone className="h-10 w-32 rounded-vb-lg" />
            </div>
            <div className="mt-auto border-t border-vb-line bg-vb-bg-subtle px-7 py-4">
              <Bone className="vb-skel-line vb-skel-line--sm w-[86px]" />
              <Bone className="vb-skel-line mt-2 w-[58%]" />
            </div>
          </div>
        </div>

        {/* "Here's how you stand." + the tier accordion. */}
        <div className="space-y-4">
          <Bone className="h-[19px] w-[188px]" />
          <div className="vb-tieracc">
            {[0, 1, 2].map((tier) => (
              <div key={tier} className={cn("vb-tier", tier === 0 && "vb-tier--open")}>
                <div className="vb-tier__head">
                  <Bone className="h-[14px] w-[14px] rounded-vb-xs" />
                  <span className="vb-tier__meta">
                    <Bone className="h-[15px] w-[132px]" />
                    <Bone className="vb-skel-line vb-skel-line--sm mt-1 w-[188px]" />
                  </span>
                  <span className="vb-tier__counts">
                    {[0, 1, 2].map((c) => (
                      <Bone key={c} className="h-[13px] w-[34px]" />
                    ))}
                  </span>
                  <Bone className="h-[24px] w-[40px]" />
                </div>
                <div className="vb-tier__body">
                  <div className="vb-sigcard-grid">
                    {[0, 1, 2, 3, 4, 5].map((card) => (
                      <div key={card} className="vb-sigcard">
                        <span className="vb-sigcard__top">
                          <Bone className="vb-sigcard__nm h-[13px]" style={{ maxWidth: 150 }} />
                          <Bone className="vb-sigcard__badge h-[18px] w-[52px] rounded-vb-pill" />
                        </span>
                        <Bone className="vb-skel-line vb-skel-line--sm mt-1 w-full" />
                        <Bone className="vb-skel-line vb-skel-line--sm w-[72%]" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reputation readout closes the dashboard. */}
        <div className="rounded-2xl border border-vb-line bg-vb-surface p-5 shadow-vb-sm sm:p-6">
          <Bone className="h-[18px] w-[262px]" />
          <Bone className="mt-4 h-[34px] w-full rounded-vb-md" />
          <div className="mt-4 flex flex-col gap-2">
            <Bone className="vb-skel-line w-[88%]" />
            <Bone className="vb-skel-line w-[54%]" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Settings — page header + the sticky rail / content column.
 *
 * Plain `.vb-page`, no `--narrow`: that is what Settings.tsx renders, and the
 * content column caps itself at 1000px via `.vb-settings__content`. Adding the
 * 1064px wrapper cap here made the skeleton narrower than the page it stands in
 * for, so the whole column jumped left-to-right on hand-off.
 */
function SettingsSkeleton() {
  return (
    <div className="vb-page">
      <HeaderBones titleWidth="34%" subtitle actions={1} />
      <div className="vb-settings">
        <div className="vb-settings__rail">
          <div className="flex flex-col gap-1.5">
            <Bone className="vb-skel-line vb-skel-line--sm mb-1 w-[72px]" />
            {[0, 1, 2, 3, 4].map((i) => (
              <Bone key={i} className="h-[34px] w-full rounded-vb-md" />
            ))}
            <Bone className="vb-skel-line vb-skel-line--sm mb-1 mt-3 w-[62px]" />
            <Bone className="h-[34px] w-full rounded-vb-md" />
          </div>
        </div>
        <div className="vb-settings__content">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="rounded-vb-lg border border-vb-line bg-vb-surface p-5 shadow-vb-sm"
            >
              <Bone className="h-[18px] w-[164px]" />
              <Bone className="vb-skel-line vb-skel-line--sm mt-2 w-[68%]" />
              <div className="mt-5 flex flex-col gap-3">
                <Bone className="vb-skel-field w-full" />
                <Bone className="vb-skel-field w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Workbench — the fix queue beside the active-fix pane (`.wb-layout`). */
function WorkbenchSkeleton() {
  return (
    <div className="vb-root wb-page">
      <HeaderBones titleWidth="180px" subtitle actions={1} />
      <Bone className="h-[46px] w-full rounded-vb-lg" />
      <div className="wb-layout">
        <div className="flex flex-col gap-2 rounded-vb-lg border border-vb-line bg-vb-surface p-3 shadow-vb-sm">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Bone key={i} className="h-[58px] w-full rounded-vb-md" />
          ))}
        </div>
        <div className="rounded-vb-lg border border-vb-line bg-vb-surface p-6 shadow-vb-sm">
          <Bone className="h-[22px] w-[46%]" />
          <Bone className="vb-skel-line vb-skel-line--sm mt-3 w-[72%]" />
          <Bone className="mt-6 h-[120px] w-full rounded-vb-md" />
          <div className="mt-5 flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <Bone key={i} className="h-[44px] w-full rounded-vb-md" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Sources — mirrors the page's own in-page loading shape (`.src-skel`). */
function SourcesSkeleton() {
  return (
    <div className="vb-root src-page">
      <div className="src-page-hd">
        <Bone className="h-[22px] w-[210px]" />
        <Bone className="h-9 w-[132px] rounded-vb-md" />
      </div>
      <div className="src-stack">
        <div className="src-skel src-skel--readout" />
        <div className="src-skel src-skel--table" />
      </div>
    </div>
  );
}

/** Projects list — search field above a list of company rows. */
function ProjectsListSkeleton() {
  return (
    <div className="vb-page space-y-6">
      <HeaderBones titleWidth="200px" />
      <Bone className="vb-skel-field max-w-md" />
      <div className="grid gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4 rounded border p-4">
            <Bone className="h-[64px] w-[64px] flex-shrink-0 rounded" />
            <div className="flex flex-1 flex-col gap-2 pt-1.5">
              <Bone className="h-[15px] w-[38%]" />
              <Bone className="vb-skel-line vb-skel-line--sm w-[22%]" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Virtual Buyer.
 *
 * VbLayout supplies `.vb-root` for these routes normally, but it is a child of
 * the Outlet we're standing in for, so it isn't mounted yet — the wrapper has
 * to come from here.
 *
 * The four VB screens are genuinely different shapes, and each already owns a
 * skeleton for its own data load. Those are reused rather than re-approximated:
 * a second set would drift from the screens they stand in for, which is the one
 * thing a skeleton must not do. (`components/` → `pages/` is the existing
 * direction here — see Sidebar.tsx, NextThingCard.tsx.)
 */
function VbShell({ children }: { children: React.ReactNode }) {
  return <div className="vb-root min-h-full rounded-vb-lg p-4 sm:p-6">{children}</div>;
}

/** `/virtual-buyer` — BuyerOverviewPage's own wait is a header + 4 rows. */
function BuyerOverviewSkeleton() {
  return (
    <VbShell>
      <HeaderBones titleWidth="220px" subtitle actions={1} />
      <VbLoading rows={4} />
    </VbShell>
  );
}

/**
 * `/virtual-buyer/new` — the create wizard.
 *
 * Mirrors BuyerCreatePage's header plus BuyerCreateWizard's own context-loading
 * branch: `.vb-wizard` wrapping the step rail, the section header, then
 * `PickerSkeleton rails={4} lines={3}`. The picker alone sat a header-and-
 * stepper too high and dropped ~170px into place on hand-off.
 */
function BuyerCreateSkeleton() {
  return (
    <VbShell>
      <HeaderBones titleWidth="260px" subtitle />
      <div className="vb-wizard">
        <div className="steps" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="sep" />}
              <span className="step">
                <Bone className="vb-skel-chip" />
                <Bone className="h-[13px] w-[76px]" />
              </span>
            </React.Fragment>
          ))}
        </div>
        <div className="persona-hd">
          <div className="left">
            <Bone className="h-[20px] w-[52px] rounded-vb-pill" />
            <Bone className="mt-2 h-[20px] w-[280px]" />
            <Bone className="vb-skel-line vb-skel-line--sm mt-2 w-[360px]" />
          </div>
        </div>
        <PickerSkeleton rails={4} lines={3} />
      </div>
    </VbShell>
  );
}

/**
 * `/virtual-buyer/:buyerId` — header, then the 360px persona column beside the
 * detail column. The cards row lives INSIDE `.vb-detail-right`, not directly
 * under `.vb-detail`: hung off the wrong parent it spans the full width and
 * loses the persona card entirely, so the whole page slides on hand-off.
 */
function BuyerDetailSkeleton() {
  return (
    <VbShell>
      <div className="vb-detail">
        <HeaderBones titleWidth="240px" subtitle actions={2} crumb />
        <div className="vb-detail-grid">
          <div className="vb-dcard" style={{ padding: 16 }}>
            <div className="flex items-center gap-3">
              <Bone className="vb-skel-chip" />
              <Bone className="h-[16px] w-[52%]" />
            </div>
            <Bone className="vb-skel-line mt-4 w-full" />
            <Bone className="vb-skel-line mt-2 w-[86%]" />
            <Bone className="vb-skel-field mt-5 w-full" />
            <Bone className="vb-skel-field mt-3 w-full" />
          </div>
          <div className="vb-detail-right">
            <div className="vb-cards-row">
              <ExposureCardSkeleton />
              <Big5CoverageCardSkeleton />
            </div>
            <div className="vb-dcard" style={{ padding: 16 }}>
              <Bone className="h-[16px] w-[140px]" />
              {[0, 1, 2].map((i) => (
                <Bone key={i} className="mt-3 h-[44px] w-full rounded-vb-md" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </VbShell>
  );
}

/** `/virtual-buyer/:buyerId/verdict/:analysisId`. */
function BuyerVerdictSkeleton() {
  return (
    <VbShell>
      <VerdictSkeleton />
    </VbShell>
  );
}

/**
 * `/virtual-buyer/:buyerId/run/:analysisId` — a live progress screen, so there
 * is no content shape to hold; the neutral wait is the honest stand-in. (Reached
 * with `?onboarding=1` during setup, where the gate exempts it entirely.)
 */
function BuyerRunSkeleton() {
  return (
    <VbShell>
      <VbLoading rows={3} />
    </VbShell>
  );
}

/**
 * `/` — the landing resolver. It renders `.vb-page` + `VbLoading rows={2}` and
 * no header while it works out which company to open, so this matches that
 * exactly; a header here would appear and then vanish.
 */
function ResolverSkeleton() {
  return (
    <div className="vb-page">
      <VbLoading rows={2} />
    </div>
  );
}

/** Picks the shape from the path. Mirrors the route table in App.tsx. */
export function pickRouteSkeleton(pathname: string): React.FC {
  if (pathname === "/") return ResolverSkeleton;
  if (pathname.startsWith("/settings")) return SettingsSkeleton;
  if (pathname.startsWith("/projects")) return ProjectsListSkeleton;
  if (pathname.startsWith("/virtual-buyer")) {
    if (pathname === "/virtual-buyer") return BuyerOverviewSkeleton;
    if (pathname.startsWith("/virtual-buyer/new")) return BuyerCreateSkeleton;
    if (pathname.includes("/verdict/")) return BuyerVerdictSkeleton;
    if (pathname.includes("/run/")) return BuyerRunSkeleton;
    return BuyerDetailSkeleton;
  }
  if (pathname.startsWith("/reports/")) {
    if (pathname.endsWith("/sources")) return SourcesSkeleton;
    if (pathname.endsWith("/workbench")) return WorkbenchSkeleton;
    return DashboardSkeleton;
  }
  return ResolverSkeleton;
}

export function RouteSkeleton({ pathname }: { pathname: string }) {
  const Shape = pickRouteSkeleton(pathname);
  return (
    // `aria-busy` only — deliberately NOT a live region. Several shapes reuse a
    // screen's own skeleton (VbLoading, PickerSkeleton, VerdictSkeleton, the
    // detail cards) and those already carry `aria-live="polite"`; wrapping them
    // in another one nests live regions and announces the same wait twice.
    <div aria-busy="true" data-testid="route-skeleton">
      <span className="sr-only">Loading…</span>
      <Shape />
    </div>
  );
}

export default RouteSkeleton;
